/* 
Generated code: do not modify this file as it will be overwritten
Template: ImmutableJSRecord.cst

Known Issues/TODO:
- Using *Props type as Record constructor parameter is insufficient.
If a property needs to be a Record type, it should already be constructed.
- Rename Record fromJS factory method to fromJSON
- Create parseJSON method that translates JSON to Props



 */

/* eslint-disable import/order */

import { createSelector } from '@ngrx/store';
import { List, Map, Record, Seq, Set } from 'immutable';
import {
  IdListItemRecord,
  IdListItemProps,
} from 'src/store/generated/id-list-item-record.generated';
import { AppState } from 'src/store/appstate';
import { getFromOrThrow, propValueOrDefault, tryOrDefault } from 'src/util/values';

export interface PaipEventProps {
  id: string;
  reasonId: string;
  regionId: string | null;
  eventNumber: string | null;
  unitIds: List<IdListItemProps> | null;
  cardIds: List<IdListItemProps> | null;
  startTime: Date;
  endTime: Date | null;
  notes: string | null;
  companyId: string;
  dateLastUpdated: Date;
  dateCreated: Date;
  dateDeleted: Date | null;
  lastUpdatedById: string | null;
}

export class PaipEventRecord extends Record<PaipEventProps>({
  id: null as any,
  reasonId: null as any,
  regionId: null as any,
  eventNumber: null as any,
  unitIds: null as any,
  cardIds: null as any,
  startTime: null as any,
  endTime: null as any,
  notes: null as any,
  companyId: null as any,
  dateLastUpdated: null as any,
  dateCreated: null as any,
  dateDeleted: null as any,
  lastUpdatedById: null as any,
}) {
  public static fromJS(json: {
    id: string;
    reasonId: string;
    regionId: string | null;
    eventNumber: string | null;
    unitIds: any;
    cardIds: any;
    startTime: string | number | Date;
    endTime: string | number | Date | null;
    notes: string | null;
    companyId: string;
    dateLastUpdated: string | number | Date;
    dateCreated: string | number | Date;
    dateDeleted: string | number | Date | null;
    lastUpdatedById: string | null;
  }): PaipEventRecord {
    if (json == null) {
      throw new Error('PaipEventRecord.fromJS: parameter cannot be null');
    }
    return new PaipEventRecord({
      id: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'id', '00000000-0000-0000-0000-000000000000'),
        'PaipEventJson.id',
      ),
      reasonId: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'reasonId', '00000000-0000-0000-0000-000000000000'),
        'PaipEventJson.reasonId',
      ),
      regionId: json.regionId,
      eventNumber: json.eventNumber,
      unitIds:
        json.unitIds != null
          ? List<IdListItemRecord>(json.unitIds.map((o: any) => new IdListItemRecord(o)))
          : null,
      cardIds:
        json.cardIds != null
          ? List<IdListItemRecord>(json.cardIds.map((o: any) => new IdListItemRecord(o)))
          : null,
      startTime: new Date(
        getFromOrThrow(
          json,
          (j) => propValueOrDefault(j, 'startTime', '0001-01-01T00:00:00.00Z'),
          'PaipEventJson.startTime',
        ),
      ),
      endTime: json.endTime != null ? new Date(json.endTime) : null,
      notes: json.notes,
      companyId: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'companyId', '00000000-0000-0000-0000-000000000000'),
        'PaipEventJson.companyId',
      ),
      dateLastUpdated: new Date(json.dateLastUpdated),
      dateCreated: new Date(json.dateCreated),
      dateDeleted: json.dateDeleted != null ? new Date(json.dateDeleted) : null,
      lastUpdatedById: json.lastUpdatedById,
    });
  }

  public static fromJSUnsafe(json: {
    id: string;
    reasonId: string;
    regionId: string | null;
    eventNumber: string | null;
    unitIds: any;
    cardIds: any;
    startTime: string | number | Date;
    endTime: string | number | Date | null;
    notes: string | null;
    companyId: string;
    dateLastUpdated: string | number | Date;
    dateCreated: string | number | Date;
    dateDeleted: string | number | Date | null;
    lastUpdatedById: string | null;
  }): [PaipEventRecord, Error[]] {
    if (json == null) {
      throw new Error('PaipEventRecord.fromJSUnsafe: parameter cannot be null');
    }
    const errors: Error[] = [];

    const record = new PaipEventRecord({
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      id: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'id', '00000000-0000-0000-0000-000000000000'),
            'PaipEventJson.id',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      reasonId: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'reasonId', '00000000-0000-0000-0000-000000000000'),
            'PaipEventJson.reasonId',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      regionId: tryOrDefault(
        () => json.regionId,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      eventNumber: tryOrDefault(
        () => json.eventNumber,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      unitIds: tryOrDefault(
        () =>
          json.unitIds != null
            ? List<IdListItemRecord>(json.unitIds.map((o: any) => new IdListItemRecord(o)))
            : null,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      cardIds: tryOrDefault(
        () =>
          json.cardIds != null
            ? List<IdListItemRecord>(json.cardIds.map((o: any) => new IdListItemRecord(o)))
            : null,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      startTime: tryOrDefault(
        () =>
          new Date(
            getFromOrThrow(
              json,
              (j) => propValueOrDefault(j, 'startTime', '0001-01-01T00:00:00.00Z'),
              'PaipEventJson.startTime',
            ),
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      endTime: tryOrDefault(
        () => (json.endTime != null ? new Date(json.endTime) : null),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      notes: tryOrDefault(
        () => json.notes,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      companyId: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'companyId', '00000000-0000-0000-0000-000000000000'),
            'PaipEventJson.companyId',
          ),
        (err) => errors.push(err),
      )!,
      dateLastUpdated: new Date(json.dateLastUpdated),
      dateCreated: new Date(json.dateCreated),
      dateDeleted: json.dateDeleted != null ? new Date(json.dateDeleted) : null,
      lastUpdatedById: json.lastUpdatedById,
    });

    return [record, errors];
  }

  constructor(props: PaipEventProps) {
    super(props);
    if (props == null) {
      throw new Error('PaipEventRecord constructor parameter cannot be null');
    }
  }
}

export abstract class PaipEventViewModelBase implements PaipEventProps {
  protected readonly _props: PaipEventProps;
  private _json?: PaipEventProps;

  get id(): string {
    return this._props.id;
  }
  get reasonId(): string {
    return this._props.reasonId;
  }
  get regionId(): string | null {
    return this._props.regionId;
  }
  get eventNumber(): string | null {
    return this._props.eventNumber;
  }
  get unitIds(): List<IdListItemProps> | null {
    return this._props.unitIds;
  }
  get cardIds(): List<IdListItemProps> | null {
    return this._props.cardIds;
  }
  get startTime(): Date {
    return this._props.startTime;
  }
  get endTime(): Date | null {
    return this._props.endTime;
  }
  get notes(): string | null {
    return this._props.notes;
  }
  get companyId(): string {
    return this._props.companyId;
  }

  get dateCreated(): Date {
    return this._props.dateCreated;
  }
  get dateLastUpdated(): Date {
    return this._props.dateLastUpdated;
  }
  get dateDeleted(): Date | null {
    return this._props.dateDeleted;
  }
  get lastUpdatedById(): string | null {
    return this._props.lastUpdatedById;
  }

  constructor(props: PaipEventProps) {
    this._props = props;
  }

  public toJS() {
    return this.toJSON();
  }

  public toJSON() {
    if (this._json == null) {
      this._json = {
        id: this._props.id,
        reasonId: this._props.reasonId,
        regionId: this._props.regionId,
        eventNumber: this._props.eventNumber,
        unitIds: this._props.unitIds,
        cardIds: this._props.cardIds,
        startTime: this._props.startTime,
        endTime: this._props.endTime,
        notes: this._props.notes,
        companyId: this._props.companyId,
        dateLastUpdated: this._props.dateLastUpdated,
        dateCreated: this._props.dateCreated,
        dateDeleted: this._props.dateDeleted,
        lastUpdatedById: this._props.lastUpdatedById,
      };
    }
    return this._json;
  }
}

//#region Selectors

export const selectAllPaipEventEntities = (state: AppState) =>
  state.entityStores.paipEvent.entities;

export const selectPaipEventById = (id: string) => (state: AppState) =>
  state.entityStores.paipEvent.entities.get(id);

export const selectPaipEventMetadataById = (id: string) => (state: AppState) =>
  state.entityStores.paipEvent.meta.get(id);

export const selectPaipEventAndMetadataById = (id: string) =>
  createSelector(selectPaipEventById(id), selectPaipEventMetadataById(id), (paipEvent, meta) => ({
    paipEvent,
    meta,
  }));

export function selectPaipEventEntitiesByIds(ids: string[]) {
  return createSelector(
    selectAllPaipEventEntities,

    (entities) => {
      // TODO: Once we're on TS 3.5+, I don't think we'll need use type assertions
      const entries = ids.map((id) => [id, entities.get(id)]) as unknown as Iterable<
        [string, PaipEventRecord | undefined]
      >;
      return Map(entries);
    },
  );
}

export function selectPaipEventEntitiesByReasonId(reasonId: string) {
  return createSelector(selectAllPaipEventEntities, (entities) =>
    entities.filter((e) => e.reasonId === reasonId).valueSeq(),
  );
}

export function selectPaipEventEntitiesByRegionId(regionId: string) {
  return createSelector(selectAllPaipEventEntities, (entities) =>
    entities.filter((e) => e.regionId === regionId).valueSeq(),
  );
}

export function selectPaipEventEntitiesByEventNumber(eventNumber: string) {
  return createSelector(selectAllPaipEventEntities, (entities) =>
    entities.filter((e) => e.eventNumber === eventNumber).valueSeq(),
  );
}

export function selectPaipEventEntitiesByStartTime(startTime: Date) {
  return createSelector(selectAllPaipEventEntities, (entities) =>
    entities.filter((e) => e.startTime === startTime).valueSeq(),
  );
}

export function selectPaipEventEntitiesByEndTime(endTime: Date) {
  return createSelector(selectAllPaipEventEntities, (entities) =>
    entities.filter((e) => e.endTime === endTime).valueSeq(),
  );
}

export function selectPaipEventEntitiesByNotes(notes: string) {
  return createSelector(selectAllPaipEventEntities, (entities) =>
    entities.filter((e) => e.notes === notes).valueSeq(),
  );
}

export function selectPaipEventEntitiesByCompanyId(companyId: string) {
  return createSelector(selectAllPaipEventEntities, (entities) =>
    entities.filter((e) => e.companyId === companyId).valueSeq(),
  );
}
//#endregion Selectors
